import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { formatCents } from '@pirouette/shared';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Empty } from '@/components/ui/Empty';
import { useDances } from '@/data/productions';
import {
  useCompetition,
  useCompetitionCommandCenter,
  useCompetitionResultsDetail,
  useRecordResult,
  type CompetitionCommandCenter,
  type CompetitionResultDetailRow,
} from '@/data/competitions';
import { toast } from '@/lib/toast';

// Competition Command Center. A child of the competition detail route (like
// routines/$routineId/media), so it inherits the /admin `admin`-role guard.
// Mirrors the Recital Ops dashboard (admin.productions.$id.recital):
//   1. Summary header — StatCards from competition_command_center (entries,
//      dancers, fees, scheduled, itinerary items, results recorded), reusing
//      the same StatCard primitive so it reads as one product.
//   2. Results table — rows from competition_results_detail (routine, division
//      / level, schedule, placement / score / award) with an inline "record
//      result" action that upserts via useRecordResult. Dance names resolve
//      client-side from the admin dance list.
export const Route = createFileRoute('/admin/competitions/$id/command-center')({
  component: CommandCenter,
});

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ---------------------------------------------------------------------------
// Layout primitives (mirror admin.productions.$id.recital / admin.bi.index)
// ---------------------------------------------------------------------------

function StatCard({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <article className="surface-elevated p-5">
      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{label}</p>
      <p className="mt-2 font-[var(--font-display)] text-[var(--text-3xl)] tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">{hint}</p>}
    </article>
  );
}

function Section({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-4">
      <div>
        <h2 id={`${id}-heading`} className="text-[var(--text-xl)]">
          {title}
        </h2>
        <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          {description}
        </p>
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Summary header
// ---------------------------------------------------------------------------

function SummaryGrid({ summary }: { summary: CompetitionCommandCenter }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <StatCard label="Entries" value={summary.entries} hint="Routines entered" />
      <StatCard label="Dancers" value={summary.dancers} hint="Across all routines" />
      <StatCard
        label="Entry fees"
        value={formatCents(summary.total_fees_cents)}
        hint="Total across entries"
      />
      <StatCard label="Scheduled" value={summary.scheduled} hint="Routines with a time" />
      <StatCard
        label="Itinerary items"
        value={summary.itinerary_items}
        hint="Calls, travel, etc."
      />
      <StatCard
        label="Results recorded"
        value={summary.results_recorded}
        hint={`of ${summary.entries} entries`}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Results table
// ---------------------------------------------------------------------------

// Generated row fields are typed non-null, but unrecorded routines come back
// null from the LEFT JOIN — coerce defensively (same pattern recital uses).
function placementTone(placement: number | null): BadgeTone {
  if (placement !== null && placement <= 3) return 'success';
  return 'neutral';
}

function formatScheduled(scheduledAt: string | null): string {
  if (!scheduledAt) return 'Unscheduled';
  const date = new Date(scheduledAt);
  return Number.isNaN(date.getTime()) ? 'Unscheduled' : date.toLocaleString();
}

interface ResultForm {
  placement: string;
  score: string;
  award: string;
}

function emptyForm(row: CompetitionResultDetailRow): ResultForm {
  return {
    placement: row.placement != null ? String(row.placement) : '',
    score: row.score != null ? String(row.score) : '',
    award: row.award ?? '',
  };
}

function ResultRow({
  row,
  danceName,
  competitionId,
}: {
  row: CompetitionResultDetailRow;
  danceName: string;
  competitionId: string;
}) {
  const record = useRecordResult();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ResultForm>(() => emptyForm(row));

  const placement = row.placement ?? null;
  const score = row.score ?? null;
  const award = row.award ?? null;
  const hasResult = placement !== null || score !== null || (award ?? '') !== '';

  function startEdit() {
    setForm(emptyForm(row));
    setEditing(true);
  }

  async function handleSave() {
    try {
      await record.mutateAsync({
        competition_id: competitionId,
        input: {
          competition_routine_id: row.routine_id,
          placement: form.placement.trim() ? Number.parseInt(form.placement, 10) : null,
          score: form.score.trim() ? Number.parseFloat(form.score) : null,
          award: form.award.trim() || null,
        },
      });
      setEditing(false);
      toast({ title: 'Result recorded', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <li className="surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="font-medium">{danceName}</p>
          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {row.division || '—'}
            {row.level ? ` · ${row.level}` : ''}
            {' · '}
            {formatScheduled(row.scheduled_at)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {placement !== null && <Badge tone={placementTone(placement)}>#{placement}</Badge>}
          {score !== null && <Badge tone="info">{score.toFixed(2)}</Badge>}
          {award && <Badge tone="accent">{award}</Badge>}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => (editing ? setEditing(false) : startEdit())}
          >
            {editing ? 'Cancel' : hasResult ? 'Edit result' : 'Record result'}
          </Button>
        </div>
      </div>

      {editing && (
        <div className="mt-3 grid gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3 sm:grid-cols-4">
          <div>
            <Label htmlFor={`cc-pl-${row.routine_id}`}>Placement</Label>
            <Input
              id={`cc-pl-${row.routine_id}`}
              type="number"
              min="1"
              value={form.placement}
              onChange={(e) => setForm({ ...form, placement: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor={`cc-sc-${row.routine_id}`}>Score</Label>
            <Input
              id={`cc-sc-${row.routine_id}`}
              type="number"
              step="0.01"
              min="0"
              value={form.score}
              onChange={(e) => setForm({ ...form, score: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor={`cc-aw-${row.routine_id}`}>Award</Label>
            <Input
              id={`cc-aw-${row.routine_id}`}
              value={form.award}
              onChange={(e) => setForm({ ...form, award: e.target.value })}
              placeholder="Platinum, High Gold…"
            />
          </div>
          <div className="sm:col-span-4">
            <Button onClick={() => void handleSave()} disabled={record.isPending}>
              {record.isPending ? 'Saving…' : 'Save result'}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function ResultsList({
  rows,
  danceName,
  competitionId,
}: {
  rows: CompetitionResultDetailRow[];
  danceName: (danceId: string) => string;
  competitionId: string;
}) {
  return (
    <ul className="grid gap-3">
      {rows.map((row) => (
        <ResultRow
          key={row.routine_id}
          row={row}
          danceName={danceName(row.dance_id)}
          competitionId={competitionId}
        />
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Command Center
// ---------------------------------------------------------------------------

function CommandCenter() {
  const { id } = Route.useParams();
  const competition = useCompetition(id);
  const summary = useCompetitionCommandCenter(id);
  const detail = useCompetitionResultsDetail(id);
  // All admin-visible dances; the detail rows carry dance_id, resolved here
  // client-side (same source the routines picker uses).
  const dances = useDances(undefined);

  const danceNameById = useMemo(
    () => new Map((dances.data ?? []).map((d) => [d.id, d.name])),
    [dances.data],
  );
  const danceName = (danceId: string): string => danceNameById.get(danceId) ?? 'Unknown routine';

  const summaryData = summary.data;
  const detailRows = detail.data ?? [];

  return (
    <div className="space-y-10">
      <header>
        <Link
          to="/admin/competitions/$id"
          params={{ id }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Competition
        </Link>
        <p className="mt-2 tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Command center
        </p>
        <h1 className="mt-1">{competition.data?.name ?? 'Command Center'}</h1>
      </header>

      {/* Summary ---------------------------------------------------------- */}
      <Section
        id="summary"
        title="At a glance"
        description="Entries and dancers, total entry fees, how many routines are scheduled, itinerary items planned, and results recorded so far."
      >
        {summary.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : summary.error || !summaryData ? (
          <Empty
            title="Summary unavailable"
            description="The command-center summary could not be loaded for this competition."
          />
        ) : (
          <SummaryGrid summary={summaryData} />
        )}
      </Section>

      {/* Routines + results ----------------------------------------------- */}
      <Section
        id="results"
        title="Routines & results"
        description="Every entered routine with its division, level, and schedule. Record an overall placement, score, and award inline — top-3 placements are flagged."
      >
        {detail.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : detail.error ? (
          <Empty
            title="Results unavailable"
            description="The routine results could not be loaded for this competition."
          />
        ) : detailRows.length === 0 ? (
          <Empty
            title="No routines entered yet"
            description="Add routines on the competition page to start recording results here."
          />
        ) : (
          <ResultsList rows={detailRows} danceName={danceName} competitionId={id} />
        )}
      </Section>
    </div>
  );
}
